import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { TranslocoRootModule } from 'app/transloco-root.module';
import { IgntrIconComponent, IconName } from 'app/shared/igntr';
import { TenantFeatures } from 'app/state/feature.repository';

interface DayChip {
  icon: IconName;
  labelKey: string;
  value: string;
  subKey?: string;
  tone: 'neutral' | 'brand' | 'success';
  /** When set, the chip is only shown if the tenant has this feature. */
  feature?: TenantFeatures;
}

interface QuickAction {
  icon: IconName;
  labelKey: string;
  subKey: string;
  link?: string;
  disabled?: boolean;
  primary?: boolean;
  /** When set, the action is only shown if the tenant has this feature. */
  feature?: TenantFeatures;
}

@Component({
  selector: 'app-dashboard-hero',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    TranslocoRootModule,
    IgntrIconComponent,
  ],
  template: `
    <div
      class="relative overflow-hidden rounded-2xl bg-white"
      style="border: 1px solid rgba(226,232,240,0.8);"
    >
      <div
        class="absolute -top-32 -right-32 w-[480px] h-[480px] rounded-full bg-gradient-to-br from-accent/10 to-transparent blur-3xl pointer-events-none"
      ></div>
      <div
        class="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-accent via-brand to-brand-hot"
      ></div>

      <!-- Mobile gets a compact greeting (small eyebrow, ~22px headline,
           tight padding) so the hero doesn't fill the phone screen; from md
           up it keeps the original large serif layout. -->
      <div
        class="relative px-4 pt-3.5 pb-3.5 md:px-7 md:pt-7 md:pb-6 grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-3 md:gap-6 items-end"
      >
        <div>
          <div
            class="inline-flex items-center gap-2 text-[11px] md:text-[14px] uppercase tracking-[0.14em] text-slate-500 font-medium whitespace-nowrap"
          >
            <span class="w-1.5 h-1.5 rounded-full bg-accent"></span>
            {{ dateLabel }} · {{ timeLabel }}
          </div>
          <h1
            class="mt-1.5 md:mt-3 font-serif text-[22px] sm:text-[26px] md:text-[36px] leading-[1.15] md:leading-[1.05] font-semibold tracking-tight text-slate-900"
          >
            {{ 'dashGreetingMorning' | transloco
            }}{{ userName ? ', ' + userName : '' }}.
          </h1>
          <p
            class="mt-1.5 md:mt-3 text-[13px] md:text-[14px] text-slate-600 leading-relaxed max-w-[58ch]"
          >
            {{
              summaryKey
                | transloco: { meetings: meetingsCount, learning: learningPct }
            }}
          </p>
        </div>

        @if (chips.length && !minimal) {
        <div class="flex flex-wrap gap-[10px] lg:justify-end">
          @for (chip of chips; track chip.labelKey) {
            <div
              class="px-[14px] py-[10px] rounded-xl flex items-center gap-3 min-w-[148px]"
              [class]="chipClasses(chip.tone)"
              [style.border]="chipBorder(chip.tone)"
            >
              <span class="opacity-70">
                <igntr-icon [name]="chip.icon" [size]="16"></igntr-icon>
              </span>
              <div>
                <div class="text-[14px] uppercase tracking-[0.12em] opacity-80">
                  {{ chip.labelKey | transloco }}
                </div>
                <div class="flex items-baseline gap-1.5">
                  <span class="text-[14px] font-semibold tabular-nums">{{
                    chip.value
                  }}</span>
                  @if (chip.subKey) {
                    <span class="text-[14px] opacity-70">{{
                      chip.subKey | transloco
                    }}</span>
                  }
                </div>
              </div>
            </div>
          }
        </div>
        }
      </div>

      <!-- Tenant "Information" content is projected here, directly under the
           greeting/summary and above the quick-actions. Renders nothing when
           the host doesn't project anything. -->
      <ng-content></ng-content>

      @if (!minimal) {
      <div
        class="relative px-4 pb-5 pt-3 md:px-7 md:pb-6 flex flex-wrap gap-2"
        style="border-top: 1px solid rgba(241,245,249,0.8);"
      >
        @for (action of visibleActions; track action.labelKey) {
          @if (action.disabled) {
            <button
              type="button"
              disabled
              [title]="'dashComingSoon' | transloco"
              class="group inline-flex items-center gap-[10px] px-[14px] py-2 rounded-lg cursor-not-allowed opacity-60"
              style="border: 1px solid rgba(226,232,240,0.8); background: white;"
            >
              <span
                class="w-7 h-7 rounded-lg flex items-center justify-center shrink-0 bg-slate-100 text-slate-600"
              >
                <span class="scale-90 inline-flex">
                  <igntr-icon [name]="action.icon" [size]="16"></igntr-icon>
                </span>
              </span>
              <span class="text-left">
                <span class="block text-[14px] font-medium text-slate-900">{{
                  action.labelKey | transloco
                }}</span>
                <span class="block text-[14px] text-slate-500">{{
                  'dashComingSoon' | transloco
                }}</span>
              </span>
            </button>
          } @else {
            <a
              [routerLink]="action.link"
              [class]="
                'group inline-flex items-center gap-[10px] px-[14px] py-2 rounded-lg transition-all ' +
                (action.primary
                  ? 'bg-gradient-to-r from-accent/10 to-accent/5 hover:from-accent/15 hover:to-accent/10'
                  : 'bg-white hover:bg-slate-50/50')
              "
              [style.border]="
                action.primary
                  ? '1px solid oklch(from var(--igntr-accent) l c h / 0.4)'
                  : '1px solid rgba(226,232,240,0.8)'
              "
            >
              <span
                [class]="
                  'w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ' +
                  (action.primary
                    ? 'bg-accent text-white'
                    : 'bg-slate-100 text-slate-600 group-hover:bg-slate-200')
                "
              >
                <span class="scale-90 inline-flex">
                  <igntr-icon [name]="action.icon" [size]="16"></igntr-icon>
                </span>
              </span>
              <span class="text-left">
                <span class="block text-[14px] font-medium text-slate-900">{{
                  action.labelKey | transloco
                }}</span>
                <span class="block text-[12px] text-slate-500">{{
                  action.subKey | transloco
                }}</span>
              </span>
              <span
                class="text-slate-300 group-hover:text-accent transition-colors ml-1"
              >
                <igntr-icon name="arrowR" [size]="16"></igntr-icon>
              </span>
            </a>
          }
        }
      </div>
      }
    </div>
  `,
})
export class DashboardHeroComponent {
  @Input() userName: string | null = null;
  @Input() meetingsCount = 0;
  @Input() learningPct = 0;
  /** Features enabled for the active tenant; actions tagged with a feature
   *  not in this list are hidden. */
  @Input() enabledFeatures: TenantFeatures[] = [];
  /** Greeting-only mode: hides the stat chips and the quick-action row (used by
   *  tenant homes that render their own content below, e.g. IGNTR). */
  @Input() minimal = false;

  readonly dateLabel: string;
  readonly timeLabel: string;

  constructor() {
    const now = new Date();
    const locale =
      (localStorage.getItem('curUserLang') || 'en') === 'da'
        ? 'da-DK'
        : 'en-US';
    this.dateLabel = now.toLocaleDateString(locale, {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
    });
    this.timeLabel = now.toLocaleTimeString(locale, {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  }

  /** Subtitle is feature-aware: it only asserts stats for features the tenant
   *  actually has. "meetings" needs Calendar, "learning" needs Sprints — a
   *  tenant like Danica with neither gets a neutral overview line instead of a
   *  sentence about meetings/learning it doesn't have. */
  get summaryKey(): string {
    const hasMeetings = this.enabledFeatures.includes(TenantFeatures.Calendar);
    const hasLearning = this.enabledFeatures.includes(TenantFeatures.Sprints);
    if (hasMeetings && hasLearning) return 'dashHeroSummaryRep';
    if (hasMeetings) return 'dashHeroSummaryMeetings';
    if (hasLearning) return 'dashHeroSummaryLearning';
    return 'dashHeroSummaryNone';
  }

  get chips(): DayChip[] {
    const all: DayChip[] = [
      {
        icon: 'calendar',
        labelKey: 'dashChipMeetings',
        value: String(this.meetingsCount),
        tone: 'neutral',
        feature: TenantFeatures.Calendar,
      },
    ];
    return all.filter(
      (c) => !c.feature || this.enabledFeatures.includes(c.feature),
    );
  }

  readonly actions: QuickAction[] = [
    {
      icon: 'sparkle',
      labelKey: 'dashActionPrepMeetings',
      subKey: 'dashActionPrepMeetingsSub',
      link: '/ai-advanced-pilot',
      primary: true,
      feature: TenantFeatures.AIAssistant,
    },
    {
      icon: 'play',
      labelKey: 'dashActionContinueLearning',
      subKey: 'dashActionContinueLearningSub',
      link: '/sprints',
      feature: TenantFeatures.Sprints,
    },
    {
      icon: 'target',
      labelKey: 'dashActionRoleplay',
      subKey: 'dashActionRoleplaySub',
      link: '/ai-roleplay',
      feature: TenantFeatures.AIRoleplay,
    },
    {
      icon: 'bulb',
      labelKey: 'dashActionReflect',
      subKey: 'dashActionReflectSub',
      link: '/calendar',
      feature: TenantFeatures.Calendar,
    },
  ];

  /** Actions whose feature is enabled for the tenant (untagged actions always
   *  show). */
  get visibleActions(): QuickAction[] {
    return this.actions.filter(
      (a) => !a.feature || this.enabledFeatures.includes(a.feature),
    );
  }

  chipClasses(tone: DayChip['tone']): string {
    switch (tone) {
      case 'brand':
        return 'bg-accent/10 text-accent-deep';
      case 'success':
        return 'bg-emerald-50/70 text-emerald-700';
      default:
        return 'bg-slate-50/60 text-slate-700';
    }
  }

  chipBorder(tone: DayChip['tone']): string {
    switch (tone) {
      case 'brand':
        return '1px solid oklch(from var(--igntr-accent) l c h / 0.3)';
      case 'success':
        return '1px solid #a7f3d0';
      default:
        return '1px solid #e2e8f0';
    }
  }
}
